import { useMemo } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query';

import {
  getNotifications,
  markAllAsRead as markAllAsReadApi,
  markAsRead as markAsReadApi,
} from '@/services/notificationService';

import type { NotificationDto, NotificationListResponse } from '@/types/notification';

// ============================================
// CONSTANTS
// ============================================

export const NOTIFICATIONS_QUERY_KEY = ['notifications'] as const;
export const UNREAD_COUNT_QUERY_KEY = ['notificationUnreadCount'] as const;
export const NOTIFICATIONS_PAGE_SIZE = 20;

// ============================================
// TYPES
// ============================================

type NotificationPages = InfiniteData<NotificationListResponse>;

// ============================================
// CACHE HELPERS (exported for SSE consumers)
// ============================================

/**
 * Prepend a notification to page 0 of the infinite query cache.
 * Dedupes by id (no-op if the id is already on page 0).
 * Bumps page 0's `meta.total` so `getNextPageParam` math stays consistent.
 *
 * Use this from SSE handlers — avoids refetching every loaded page.
 */
export function prependNotificationToCache(
  queryClient: QueryClient,
  notification: NotificationDto,
): void {
  queryClient.setQueryData<NotificationPages>(NOTIFICATIONS_QUERY_KEY, (prev) => {
    if (!prev || prev.pages.length === 0) return prev;

    const firstPage = prev.pages[0];
    if (!firstPage) return prev;

    if (firstPage.data.some((n) => n.id === notification.id)) return prev;

    const updatedFirstPage: NotificationListResponse = {
      data: [notification, ...firstPage.data],
      meta: { ...firstPage.meta, total: firstPage.meta.total + 1 },
    };

    return { ...prev, pages: [updatedFirstPage, ...prev.pages.slice(1)] };
  });
}

/** Bump the cached unread count by 1. Use from SSE. */
export function incrementUnreadCountInCache(queryClient: QueryClient): void {
  queryClient.setQueryData<number>(UNREAD_COUNT_QUERY_KEY, (prev) => (prev ?? 0) + 1);
}

/** Patch a single notification across every loaded page (mark as read). */
function markAsReadInCache(queryClient: QueryClient, id: string): void {
  queryClient.setQueryData<NotificationPages>(NOTIFICATIONS_QUERY_KEY, (prev) => {
    if (!prev) return prev;
    const now = new Date().toISOString();
    return {
      ...prev,
      pages: prev.pages.map((page) => ({
        ...page,
        data: page.data.map((n) =>
          n.id === id && !n.read_at ? { ...n, read_at: now, status: 'read' as const } : n,
        ),
      })),
    };
  });
}

/** Mark all notifications across every loaded page as read. */
function markAllReadInCache(queryClient: QueryClient): void {
  queryClient.setQueryData<NotificationPages>(NOTIFICATIONS_QUERY_KEY, (prev) => {
    if (!prev) return prev;
    const now = new Date().toISOString();
    return {
      ...prev,
      pages: prev.pages.map((page) => ({
        ...page,
        data: page.data.map((n) =>
          n.read_at ? n : { ...n, read_at: now, status: 'read' as const },
        ),
      })),
    };
  });
}

// ============================================
// HOOK
// ============================================

/**
 * Notification list with infinite scroll.
 *
 * Source of truth = TanStack Query cache.
 *  - List:  ['notifications']           (infinite query, owned here)
 *  - Count: ['notificationUnreadCount'] (regular query, owned by NotificationBell)
 *
 * Both caches are updated via `setQueryData` from mark mutations and from the
 * SSE handler in AppShell. There is no Zustand mirror and no refetch fan-out.
 */
export function useNotifications() {
  const queryClient = useQueryClient();

  const query = useInfiniteQuery({
    queryKey: NOTIFICATIONS_QUERY_KEY,
    queryFn: ({ pageParam }) =>
      getNotifications({ page: pageParam, limit: NOTIFICATIONS_PAGE_SIZE }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _all, lastPageParam) => {
      const loaded = lastPageParam * NOTIFICATIONS_PAGE_SIZE;
      return loaded < lastPage.meta.total ? lastPageParam + 1 : undefined;
    },
  });

  // Flatten + dedupe by id. SSE prepends + offset-based pagination can produce
  // the same id on adjacent pages when a new row shifts page boundaries.
  const notifications = useMemo<NotificationDto[]>(() => {
    if (!query.data) return [];
    const seen = new Set<string>();
    const out: NotificationDto[] = [];
    for (const page of query.data.pages) {
      for (const n of page.data) {
        if (seen.has(n.id)) continue;
        seen.add(n.id);
        out.push(n);
      }
    }
    return out;
  }, [query.data]);

  // ── Mutations ──

  const markAsReadMutation = useMutation({
    mutationFn: markAsReadApi,
    onMutate: async (id: string) => {
      // Cancel in-flight refetches so they don't overwrite our optimistic patch.
      await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
      await queryClient.cancelQueries({ queryKey: UNREAD_COUNT_QUERY_KEY });

      const previousPages = queryClient.getQueryData<NotificationPages>(NOTIFICATIONS_QUERY_KEY);
      const previousCount = queryClient.getQueryData<number>(UNREAD_COUNT_QUERY_KEY);

      // Derive wasUnread from the cache snapshot — avoids stale-closure races
      // with the memoised `notifications` array.
      const wasUnread =
        previousPages?.pages.some((p) => p.data.some((n) => n.id === id && !n.read_at)) ?? false;

      markAsReadInCache(queryClient, id);
      if (wasUnread) {
        queryClient.setQueryData<number>(UNREAD_COUNT_QUERY_KEY, (n) =>
          Math.max(0, (n ?? 0) - 1),
        );
      }

      return { previousPages, previousCount };
    },
    onError: (_err, _id, context) => {
      if (context?.previousPages) {
        queryClient.setQueryData(NOTIFICATIONS_QUERY_KEY, context.previousPages);
      }
      if (context?.previousCount !== undefined) {
        queryClient.setQueryData(UNREAD_COUNT_QUERY_KEY, context.previousCount);
      }
    },
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: markAllAsReadApi,
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
      await queryClient.cancelQueries({ queryKey: UNREAD_COUNT_QUERY_KEY });

      const previousPages = queryClient.getQueryData<NotificationPages>(NOTIFICATIONS_QUERY_KEY);
      const previousCount = queryClient.getQueryData<number>(UNREAD_COUNT_QUERY_KEY);

      markAllReadInCache(queryClient);
      queryClient.setQueryData<number>(UNREAD_COUNT_QUERY_KEY, 0);

      return { previousPages, previousCount };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousPages) {
        queryClient.setQueryData(NOTIFICATIONS_QUERY_KEY, context.previousPages);
      }
      if (context?.previousCount !== undefined) {
        queryClient.setQueryData(UNREAD_COUNT_QUERY_KEY, context.previousCount);
      }
    },
  });

  return {
    notifications,
    isLoading: query.isLoading,
    isError: query.isError,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    isFetchNextPageError: query.isFetchNextPageError,
    // `mutate` is stable across renders — no useCallback needed.
    markAsRead: markAsReadMutation.mutate,
    markAllAsRead: markAllAsReadMutation.mutate,
    isMarkingAllAsRead: markAllAsReadMutation.isPending,
  };
}
